/**
 * An entity reference in the briefing. `text` (the prose default) sets the
 * name in medium weight so the paragraph scans; `chip` is a small pill in the
 * style of the Case IDE's title pill, a square initials tile then the name.
 * Hover shows the ref's kind/status card; clicking opens the entity.
 */
import { ArrowUpRight } from "lucide-react";
import type { FC, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from "@/components/ui/hover-card";
import { Tag } from "@/components/ui/tag";
import {
  kindLabel,
  parseEntityHref,
  refPersonName,
  splitRefTitle
} from "@/features/agents/refs/entityRefs";
import { initialsFrom } from "@/features/globalSearch/searchModel";
import { cn } from "@/lib/utils";

type Props = {
  name: string;
  /** App path the chip opens (`/clients/:id`, `/cases/:id`, …). */
  href: string;
  /** The markdown link title: `Kind · status · …`. */
  title?: string | null;
  variant?: "text" | "chip";
  className?: string;
};

export const EntityChip: FC<Props> = ({
  name,
  href,
  title,
  variant = "text",
  className
}) => {
  const navigate = useNavigate();
  const target = parseEntityHref(href);
  const parsed = splitRefTitle(title);
  const kind = parsed.kind ?? (target ? kindLabel(target) : null);
  const open = (e?: MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    navigate(href);
  };
  const pill =
    variant === "text" ? (
      <a
        href={href}
        onClick={open}
        data-testid="entity-chip"
        data-entity-id={target?.id ?? target?.clientId ?? target?.individualId}
        className={cn(
          "font-medium text-fg no-underline decoration-line-4 underline-offset-3 hover:underline focus-visible:underline focus-visible:outline-none",
          className
        )}
      >
        {name}
      </a>
    ) : (
      <a
        href={href}
        onClick={open}
        data-testid="entity-chip"
        data-entity-id={target?.id ?? target?.clientId ?? target?.individualId}
        className={cn(
          "inline-flex h-5.5 max-w-full items-center gap-1.5 overflow-hidden rounded-md bg-elevated pr-2 align-middle text-sm font-medium text-fg no-underline inset-ring inset-ring-line-2 select-none",
          "hover:bg-fill-1 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none data-[state=open]:bg-fill-1",
          className
        )}
      >
        <span
          aria-hidden
          className="flex h-full w-5 shrink-0 items-center justify-center bg-fill-2 text-xs font-medium text-fg-2"
        >
          {initialsFrom(
            target?.kind === "client" || target?.kind === "case"
              ? refPersonName(target.kind, name)
              : name
          ) || "?"}
        </span>
        <span className="truncate">{name}</span>
      </a>
    );
  if (!kind) return pill;
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>{pill}</HoverCardTrigger>
      <HoverCardContent className="w-72 p-3" data-testid="entity-chip-card">
        <div className="flex items-center gap-2">
          <Tag>{kind}</Tag>
          {parsed.detail ? (
            <span className="truncate text-sm text-fg-3">{parsed.detail}</span>
          ) : null}
        </div>
        <div className="mt-2 text-base font-medium text-fg">{name}</div>
        <Button size="sm" className="mt-3 w-full" onClick={() => open()}>
          Open {kind}
          <ArrowUpRight />
        </Button>
      </HoverCardContent>
    </HoverCard>
  );
};
